"use client";

import type {
  AppInsightsErrorBoundary as AppInsightsErrorBoundaryType,
  ReactPlugin as ReactPluginType,
} from "@microsoft/applicationinsights-react-js";
import type { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { getClientHost } from "@/lib/host";

/** Loaded browser SDK handles, shared as a per-JS-context singleton. */
export type AppInsightsBundle = {
  ai: ApplicationInsights;
  reactPlugin: InstanceType<typeof ReactPluginType>;
  ErrorBoundary: typeof AppInsightsErrorBoundaryType;
};

let bundle: AppInsightsBundle | null = null;
let inFlight: Promise<AppInsightsBundle | null> | null = null;

/**
 * Initialize the browser Application Insights SDK exactly once per JS context
 * and return the shared handles. Subsequent callers (e.g. an `(app)` shell
 * remount after a soft cross-group navigation) reuse the existing instance
 * instead of calling `loadAppInsights()` again — a second load would install a
 * duplicate set of global fetch/XHR/exception hooks, double-counting
 * dependency telemetry and double-injecting W3C correlation headers.
 *
 * The instance is intentionally never unloaded: it must outlive an `(app)`
 * shell unmount so the root `app/error.tsx` boundary can still report the crash
 * that unmounted it (see `trackAppInsightsException`).
 */
export function ensureAppInsights(
  connectionString: string
): Promise<AppInsightsBundle | null> {
  if (bundle) {
    return Promise.resolve(bundle);
  }
  if (inFlight) {
    return inFlight;
  }

  inFlight = (async () => {
    try {
      const [
        { ApplicationInsights, DistributedTracingModes },
        { ReactPlugin, AppInsightsErrorBoundary },
      ] = await Promise.all([
        import("@microsoft/applicationinsights-web"),
        import("@microsoft/applicationinsights-react-js"),
      ]);

      const reactPlugin = new ReactPlugin();
      const ai = new ApplicationInsights({
        config: {
          connectionString,
          extensions: [reactPlugin],
          // Page views are driven explicitly from `usePathname` in the provider
          // so they stay in lockstep with the App Router and only fire for
          // authed users.
          enableAutoRouteTracking: false,
          enableCorsCorrelation: true,
          distributedTracingMode: DistributedTracingModes.W3C,
        },
      });
      ai.loadAppInsights();

      // Stamp the surface (standalone-web / excel-add-in / word-add-in / …)
      // onto every client telemetry item's customDimensions so add-in traffic
      // can be filtered apart from the web app in App Insights.
      const host = getClientHost();
      ai.addTelemetryInitializer((envelope) => {
        envelope.data = envelope.data ?? {};
        (envelope.data as Record<string, unknown>).appSurface = host;
      });

      bundle = { ai, reactPlugin, ErrorBoundary: AppInsightsErrorBoundary };
      return bundle;
    } catch {
      // Telemetry must never break the app. A dynamic-import failure (e.g. a
      // chunk-load error after a stale deploy) leaves the SDK uninitialized;
      // clear `inFlight` so a later mount can retry instead of permanently
      // returning this rejected promise.
      inFlight = null;
      return null;
    }
  })();

  return inFlight;
}

/** Report a handled exception (e.g. Next.js `error.tsx`) to App Insights. */
export function trackAppInsightsException(
  error: Error,
  properties?: Record<string, unknown>
): void {
  // This runs inside error-boundary effects; a throw here would escalate to
  // the next-higher boundary and worsen the crash UI. Never let it throw.
  try {
    bundle?.ai.trackException({ exception: error, properties });
  } catch {
    // swallow — crash reporting must not crash the error boundary
  }
}
